import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PageHeader from '@/components/PageHeader';
import { useI18n } from '@/i18n';
import { exportMovementReport, getMovementReport, type MovementReport } from '@/services/reportsApi';

function toDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getInitialPeriod() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  return { startDate: toDateInputValue(start), endDate: toDateInputValue(end) };
}

function StatCard({ label, value, accent }: { label: string; value: string | number; accent: string }) {
  return (
    <Card sx={{ height: '100%', borderTop: '3px solid', borderTopColor: accent }}>
      <CardContent>
        <Stack spacing={1}>
          <Typography variant="overline" color="text.secondary">{label}</Typography>
          <Typography variant="h3" sx={{ fontWeight: 800 }}>{value}</Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const { t, language } = useI18n();
  const [period, setPeriod] = useState(getInitialPeriod);
  const [report, setReport] = useState<MovementReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [exportMessage, setExportMessage] = useState('');
  const [variboxSearch, setVariboxSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getMovementReport(period.startDate, period.endDate)
      .then((data) => {
        if (!cancelled) setReport(data);
      })
      .catch(() => {
        if (!cancelled) setError(t('reports.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [period.endDate, period.startDate, t]);

  const maxDailyMoves = useMemo(
    () => Math.max(1, ...(report?.daily_trend.map((item) => item.moves) ?? [])),
    [report],
  );
  const visibleVariboxes = useMemo(() => {
    const normalizedSearch = variboxSearch.trim().toLowerCase();
    const matches = report?.most_moved_variboxes.filter((item) =>
      !normalizedSearch
      || item.serial_number.toLowerCase().includes(normalizedSearch)
      || item.radex_identifier.toLowerCase().includes(normalizedSearch),
    ) ?? [];
    return normalizedSearch ? matches : matches.slice(0, 10);
  }, [report, variboxSearch]);

  const handleExport = async () => {
    setExporting(true);
    setExportMessage('');
    try {
      const file = await exportMovementReport(period.startDate, period.endDate);
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = `varibox-ruchy-${period.startDate}-${period.endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setExportMessage(t('reports.exportSuccess'));
    } catch {
      setError(t('reports.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (value: string, withTime = false) => new Date(value).toLocaleString(
    language === 'pl' ? 'pl-PL' : 'en-GB',
    withTime ? { dateStyle: 'short', timeStyle: 'short' } : { dateStyle: 'short' },
  );

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow={t('reports.eyebrow')}
        title={t('reports.title')}
        description={t('reports.description')}
        action={(
          <Button variant="contained" onClick={() => void handleExport()} disabled={loading || exporting}>
            {exporting ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
            {exporting ? t('reports.exporting') : t('reports.exportExcel')}
          </Button>
        )}
      />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mr: 1 }}>{t('reports.period')}</Typography>
          <TextField
            type="date"
            size="small"
            label={t('reports.fromDate')}
            value={period.startDate}
            onChange={(event) => setPeriod((current) => ({ ...current, startDate: event.target.value }))}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: period.endDate }}
          />
          <TextField
            type="date"
            size="small"
            label={t('reports.toDate')}
            value={period.endDate}
            onChange={(event) => setPeriod((current) => ({ ...current, endDate: event.target.value }))}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: period.startDate }}
          />
          {loading ? <CircularProgress size={22} /> : null}
        </Stack>
      </Paper>

      {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
      {exportMessage ? <Alert severity="success" onClose={() => setExportMessage('')}>{exportMessage}</Alert> : null}

      {report ? (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard label={t('reports.totalMoves')} value={report.total_moves} accent="#143f6b" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard label={t('reports.variboxesMoved')} value={report.variboxes_moved} accent="#0f766e" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard label={t('reports.averageMoves')} value={report.average_moves_per_varibox} accent="#f59e0b" />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="h6">{t('reports.movementTrendTitle')}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('reports.movementTrendDescription')}</Typography>
                    </Box>
                    {report.total_moves === 0 ? (
                      <Alert severity="info">{t('reports.noMovementData')}</Alert>
                    ) : (
                      <Box sx={{ height: 250, display: 'flex', alignItems: 'stretch', gap: 0.75, overflowX: 'auto', pb: 1 }}>
                        {report.daily_trend.map((item) => (
                          <Stack key={item.day} title={`${formatDate(item.day)}: ${item.moves}`} alignItems="center" justifyContent="flex-end" spacing={0.75} sx={{ minWidth: 24, flex: 1 }}>
                            <Typography variant="caption" color="text.secondary">{item.moves || ''}</Typography>
                            <Box sx={{ width: '100%', maxWidth: 36, minHeight: item.moves > 0 ? 6 : 2, height: `${Math.max(item.moves ? 5 : 1, (item.moves / maxDailyMoves) * 100)}%`, maxHeight: '82%', borderRadius: '6px 6px 2px 2px', bgcolor: item.moves ? 'primary.main' : 'action.disabledBackground', transition: 'height 180ms ease' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 38 }}>{formatDate(item.day)}</Typography>
                          </Stack>
                        ))}
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="h6">{t('reports.mostMovedTitle')}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('reports.mostMovedDescription')}</Typography>
                    </Box>
                    <TextField
                      size="small"
                      label={t('reports.searchVaribox')}
                      value={variboxSearch}
                      onChange={(event) => setVariboxSearch(event.target.value)}
                      fullWidth
                    />
                    {visibleVariboxes.length ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead><TableRow>
                            <TableCell>{t('reports.serialNumber')}</TableCell>
                            <TableCell>{t('reports.radex')}</TableCell>
                            <TableCell align="right">{t('reports.moveCount')}</TableCell>
                          </TableRow></TableHead>
                          <TableBody>{visibleVariboxes.map((item) => (
                            <TableRow key={`${item.serial_number}-${item.radex_identifier}`}>
                              <TableCell>{item.serial_number}</TableCell>
                              <TableCell>{item.radex_identifier}</TableCell>
                              <TableCell align="right"><Typography fontWeight={700}>{item.moves}</Typography></TableCell>
                            </TableRow>
                          ))}</TableBody>
                        </Table>
                      </TableContainer>
                    ) : <Typography variant="body2" color="text.secondary">{variboxSearch ? t('reports.noMatchingVaribox') : t('reports.noMovementData')}</Typography>}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6">{t('reports.latestMovementsTitle')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('reports.latestMovementsDescription')}</Typography>
                </Box>
                {report.recent_movements.length ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell>{t('reports.dateTime')}</TableCell>
                        <TableCell>{t('reports.serialNumber')}</TableCell>
                        <TableCell>{t('reports.route')}</TableCell>
                        <TableCell>{t('reports.actor')}</TableCell>
                      </TableRow></TableHead>
                      <TableBody>{report.recent_movements.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{formatDate(item.moved_at, true)}</TableCell>
                          <TableCell>{item.serial_number}<br /><Typography variant="caption" color="text.secondary">{item.radex_identifier}</Typography></TableCell>
                          <TableCell>{item.from_location || '—'} → {item.to_location}</TableCell>
                          <TableCell>{item.actor_name}</TableCell>
                        </TableRow>
                      ))}</TableBody>
                    </Table>
                  </TableContainer>
                ) : <Alert severity="info">{t('reports.noMovementData')}</Alert>}
              </Stack>
            </CardContent>
          </Card>
          <Typography variant="caption" color="text.secondary">{t('reports.historyStartsNotice')}</Typography>
        </>
      ) : null}
    </Stack>
  );
}